import api from './api';

export type AuditAccessSummary = {
  can_access: boolean;
  is_org_admin: boolean;
  expires_at: string | null;
};

export type AuditEmployee = {
  user_id: string;
  employee_id: string | null;
  name: string;
  email: string | null;
  avatar_url: string | null;
  department_id: string | null;
  department_name: string | null;
  designation: string | null;
  is_active: boolean;
  is_online: boolean;
  last_active_at: string | null;
  conversation_count: number;
  flagged_conversation_count: number;
  assigned_auditors: string[];
  audit_access_status: string;
};

export type AuditEmployeePage = {
  items: AuditEmployee[];
  next_cursor: string | null;
};

export type AuditEmployeeParams = {
  q?: string;
  department_id?: string;
  designation?: string;
  active?: boolean;
  assigned_auditor_id?: string;
  sort?: 'name' | 'last_active' | 'conversation_count';
  cursor?: string;
  limit?: number;
};

export type AuditParticipant = {
  user_id: string;
  name: string;
  avatar_url: string | null;
  /** The participant's company. Null when their head office has no profile yet. */
  company_name?: string | null;
  /** True when the participant sits outside the auditing admin's organization. */
  is_external?: boolean;
};

export type AuditConversation = {
  id: string;
  type: 'dm' | 'group' | 'channel';
  name: string | null;
  avatar_url: string | null;
  participants: AuditParticipant[];
  last_message: string | null;
  last_message_at: string | null;
  message_count: number;
  attachment_count: number;
  flagged: boolean;
  /** True for a cross-org (external) conversation. */
  is_external?: boolean;
  /** The other organization, when the conversation names one. */
  partner_company_name?: string | null;
};

export type AuditConversationPage = {
  items: AuditConversation[];
  next_cursor: string | null;
};

export type AuditConversationParams = {
  conversation_type?: 'dm' | 'group' | 'channel';
  participant_id?: string;
  keyword?: string;
  date_from?: string;
  date_to?: string;
  has_attachments?: boolean;
  has_edited?: boolean;
  has_deleted?: boolean;
  flagged?: boolean;
  scope?: 'all' | 'internal' | 'external';
  cursor?: string;
  limit?: number;
};

export type AuditAttachment = {
  id: string;
  file_name: string;
  file_size: number;
  mime_type: string | null;
};

export type AuditMessage = {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  sender_name: string | null;
  sender_avatar_url: string | null;
  content: string | null;
  body: Record<string, unknown> | null;
  message_type: string;
  created_at: string;
  is_edited: boolean;
  edited_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  reply_to_id: string | null;
  delivery_status: string | null;
  read_status: string | null;
  attachments: AuditAttachment[];
};

export type AuditMessagePage = {
  items: AuditMessage[];
  next_cursor: string | null;
};

export type AuditMessageParams = {
  keyword?: string;
  date_from?: string;
  date_to?: string;
  attachment_type?: string;
  include_edited?: boolean;
  include_deleted?: boolean;
  before?: string;
  limit?: number;
};

export type AuditPermission = {
  user_id: string;
  auditor_name: string | null;
  enabled: boolean;
  granted_by: string | null;
  granted_at: string | null;
  expires_at: string | null;
  reason: string | null;
  revoked_by: string | null;
  revoked_at: string | null;
};

export type AuditPermissionInput = {
  enabled: boolean;
  expires_at?: string | null;
  reason?: string | null;
};

export type AuditAssignment = {
  employee_user_id: string;
  employee_name: string | null;
  assigned_at: string | null;
  status: string;
};

export type AuditAccessRecord = {
  permission: AuditPermission;
  assignments: AuditAssignment[];
};

export type AuditAccessPage = {
  items: AuditAccessRecord[];
  next_cursor: string | null;
};

export type AuditActivity = {
  id: string;
  auditor_user_id: string | null;
  auditor_name: string | null;
  employee_user_id: string | null;
  employee_name: string | null;
  conversation_id: string | null;
  conversation_name: string | null;
  action_type: string;
  searched_keyword: string | null;
  filter_data: Record<string, unknown>;
  ip_address: string | null;
  device_information: string | null;
  created_at: string;
};

export type AuditActivityPage = {
  items: AuditActivity[];
  next_cursor: string | null;
};

export const getChatAuditAccessSummary = () =>
  api.get<AuditAccessSummary>('/audit/access/me').then((response) => response.data);

export const listAuditEmployees = (params: AuditEmployeeParams = {}) =>
  api.get<AuditEmployeePage>('/audit/employees', { params }).then((response) => response.data);

export const getAuditEmployee = (employeeUserId: string) =>
  api.get<AuditEmployee>(`/audit/employees/${employeeUserId}`).then((response) => response.data);

export const listAuditConversations = (employeeUserId: string, params: AuditConversationParams = {}) =>
  api.get<AuditConversationPage>(`/audit/employees/${employeeUserId}/conversations`, { params }).then((response) => response.data);

export const listAuditMessages = (
  employeeUserId: string,
  conversationId: string,
  params: AuditMessageParams = {},
) => api.get<AuditMessagePage>(
  `/audit/employees/${employeeUserId}/conversations/${conversationId}/messages`,
  { params },
).then((response) => response.data);

export const listAuditAccess = (params: { cursor?: string; limit?: number } = {}) =>
  api.get<AuditAccessPage>('/audit/access', { params }).then((response) => response.data);

export const listAuditAccessCandidates = (params: { q?: string; limit?: number } = {}) =>
  api.get<AuditEmployeePage>('/audit/access/candidates', { params }).then((response) => response.data);

export const updateAuditPermission = (auditorUserId: string, body: AuditPermissionInput) =>
  api.put<AuditPermission>(`/audit/access/${auditorUserId}`, body).then((response) => response.data);

export const listAuditAssignments = (auditorUserId: string) =>
  api.get<{ items: AuditAssignment[] }>(`/audit/access/${auditorUserId}/assignments`).then((response) => response.data);

export const updateAuditAssignments = (auditorUserId: string, employeeUserIds: string[]) =>
  api.put<{ items: AuditAssignment[] }>(`/audit/access/${auditorUserId}/assignments`, {
    employee_user_ids: employeeUserIds,
  }).then((response) => response.data);

export const removeAuditAssignment = (auditorUserId: string, employeeUserId: string) =>
  api.delete(`/audit/access/${auditorUserId}/assignments/${employeeUserId}`);

export const listAuditActivity = (params: {
  cursor?: string;
  limit?: number;
  auditor_user_id?: string;
  employee_user_id?: string;
  conversation_id?: string;
  action_type?: string;
} = {}) => api.get<AuditActivityPage>('/audit/activity', { params }).then((response) => response.data);
